import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu for card comment
 *
 * @prop resourceName - Menu title
 * @prop cardId - The id of the card
 * @prop commentId - The id of the comment
 * @prop reportOn - Show report action
 * @prop editOn - Show edit action
 * @prop deleteOn - Show delete action
 */

export default class CommentMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    cardId: { type: Number },
    commentId: { type: Number },
    resourceName: { type: String },
    deleteOn: { type: Boolean },
    editOn: { type: Boolean },
    reportOn: { type: Boolean },
  };

  /** @internal */
  get instanceId() {
    return `${this.cardId}_${this.commentId}`;
  }

  /** @internal */
  get baseUrl() {
    return `/cards/${this.cardId}/comments/${this.commentId}`;
  }

  /** @internal */
  get editUrl() {
    return `${this.baseUrl}/edit`;
  }

  /** @internal */
  get reportUrl() {
    return `/complaints/new?complaint[complainable_id]=${this.commentId}&complaint[complainable_type]=CardComment`;
  }

  /** @internal */
  get editItem() {
    return {
      title: this.t('actions.edit'),
      action: this.actionFor('commentExecJSFromResponse', [this.editUrl]),
      icon: 'pencil',
      partName: 'edit',
    };
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.report'),
      action: this.actionFor('commentExecJSFromResponse', [this.reportUrl]),
      icon: 'exclamation-triangle',
      partName: 'report',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('commentDelete'),
      role: 'destructive',
      icon: 'delete',
      partName: 'delete destructive',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return (this.reportOn || this.editOn) && this.deleteOn;
  }

  /** @internal */
  get items() {
    return [
      this.editOn && this.editItem,
      this.reportOn && this.reportItem,
      this.dividerOn && this.divider,
      this.deleteOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      header: this.resourceName,
      buttons: this.items,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-comment-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  commentExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  async commentDelete() {
    if (
      !this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }

    try {
      await this.commonActions.delete(this.baseUrl);
      this.dispatchEvent(
        new Event('comment:deleted', { bubbles: true, composed: true })
      );
      this.commonActions.displaySnackBarSuccess(this.t('snack.delete_success'));
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  render() {
    return html` <ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu> `;
  }
}

CommentMenu.registerTagName('bu-comment-menu');
CommentMenu.registerCommonActions('bu-comment-menu');

customElements.get('bu-comment-menu') ||
  customElements.define('bu-comment-menu', CommentMenu);
